import { useEffect, useRef } from "react";

export const LIVE_EVERY_MS = 15000;
// Coming back into view refreshes at once, but not more often than this.
const MIN_GAP_MS = 5000;

/**
 * Calls `refresh` now and then every LIVE_EVERY_MS while `enabled`, for as long
 * as someone can see the panel: not while the browser tab is in the background,
 * and not while the panel sits in a tab that isn't selected (Chakra marks an
 * inactive tab panel `hidden`). Coming back into view refreshes straight away.
 * One refresh at a time: a slow one delays the next instead of overlapping it.
 *
 * `refresh` deals with its own failures; whatever it throws is dropped here.
 */
export default function useLiveRefresh({ enabled, rootRef, refresh }) {
  const latest = useRef(refresh);
  latest.current = refresh;

  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;
    let busy = false;
    let timer = null;
    let lastStart = 0;
    const visible = () => !document.hidden && !rootRef.current?.closest("[hidden]");

    const tick = async () => {
      if (stopped || busy) return;
      clearTimeout(timer);
      if (visible()) {
        busy = true;
        lastStart = Date.now();
        try {
          await latest.current();
        } catch {
          /* refresh reports its own failures */
        }
        busy = false;
      }
      if (!stopped) timer = setTimeout(tick, LIVE_EVERY_MS);
    };

    const backInView = () => {
      if (visible() && Date.now() - lastStart >= MIN_GAP_MS) tick();
    };

    tick();
    document.addEventListener("visibilitychange", backInView);
    // Selecting the panel's tab fires no event of its own; the observer sees it appear.
    let observer = null;
    if (typeof IntersectionObserver !== "undefined" && rootRef.current) {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) backInView();
      });
      observer.observe(rootRef.current);
    }
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", backInView);
      if (observer) observer.disconnect();
    };
  }, [enabled, rootRef]);
}
